import { create } from "zustand"

// ─── Explicit popup reasons (replaces magic numbers 0, 1, 2, 4) ────────────
const LOCATION_POPUP_REASON = {
  NONE: "none",
  PERMISSION_DENIED: "permission_denied",
  SERVICE_UNAVAILABLE: "service_unavailable",
  NO_COORDINATES: "no_coordinates",
} as const

type LocationPopupReason =
  (typeof LOCATION_POPUP_REASON)[keyof typeof LOCATION_POPUP_REASON]

// ─── Store interface ────────────────────────────────────────────────────────
interface LocationPopupState {
  /** Whether the popup is currently visible */
  isVisible: boolean
  /** Whether the device's location service is unavailable (not just denied) */
  isLocationServiceUnavailable: boolean
  /** Why the popup is being shown */
  reason: LocationPopupReason
  /** Test mode flag */
  testMode?: boolean
}

interface LocationPopupActions {
  /** Show popup because user denied location permission */
  showPermissionDenied: () => void
  /** Show popup because location service is unavailable on the device */
  showServiceUnavailable: () => void
  /** Show popup because no coordinates could be resolved (Zalo fallback) */
  showNoCoordinates: () => void
  /** Dismiss / hide the popup and reset state */
  dismiss: () => void
  /** Hide popup but keep the unavailable flag (user manually set location) */
  dismissKeepUnavailable: () => void

  // ── Legacy bridge (remove after full migration) ──────────────────────────
  /**
   * @deprecated Use named actions instead:
   *   `showPermissionDenied()`, `showServiceUnavailable()`,
   *   `showNoCoordinates()`, `dismiss()`, `dismissKeepUnavailable()`
   */
  changeShow: (
    show: boolean,
    isLocationServiceUnavailable: boolean,
    code: number
  ) => void
}

type LocationPopupSetting = LocationPopupState & LocationPopupActions

// Legacy code → reason mapping (used only by the deprecated bridge)
function codeToReason(code: number): LocationPopupReason {
  switch (code) {
    case 1:
      return LOCATION_POPUP_REASON.PERMISSION_DENIED
    case 2:
      return LOCATION_POPUP_REASON.SERVICE_UNAVAILABLE
    case 4:
      return LOCATION_POPUP_REASON.NO_COORDINATES
    default:
      return LOCATION_POPUP_REASON.NONE
  }
}

// ─── Initial state ──────────────────────────────────────────────────────────
const initialState: LocationPopupState = {
  isVisible: false,
  isLocationServiceUnavailable: false,
  reason: LOCATION_POPUP_REASON.NONE,
}

// ─── Store ──────────────────────────────────────────────────────────────────
const useLocationPopupSetting = create<LocationPopupSetting>((set) => ({
  ...initialState,

  showPermissionDenied: () =>
    set({
      isVisible: true,
      isLocationServiceUnavailable: false,
      reason: LOCATION_POPUP_REASON.PERMISSION_DENIED,
    }),

  showServiceUnavailable: () =>
    set({
      isVisible: true,
      isLocationServiceUnavailable: true,
      reason: LOCATION_POPUP_REASON.SERVICE_UNAVAILABLE,
    }),

  showNoCoordinates: () =>
    set({
      isVisible: true,
      isLocationServiceUnavailable: true,
      reason: LOCATION_POPUP_REASON.NO_COORDINATES,
    }),

  dismiss: () =>
    set({
      isVisible: false,
      isLocationServiceUnavailable: false,
      reason: LOCATION_POPUP_REASON.NONE,
    }),

  dismissKeepUnavailable: () =>
    set({
      isVisible: false,
      isLocationServiceUnavailable: true,
      reason: LOCATION_POPUP_REASON.NONE,
    }),

  // Legacy bridge — delegates to the same state shape so old & new code coexist
  changeShow(show, unavailable, code) {
    set({
      isVisible: show,
      isLocationServiceUnavailable: unavailable,
      reason: codeToReason(code),
    })
  },
}))

export { useLocationPopupSetting, LOCATION_POPUP_REASON }
export type { LocationPopupReason, LocationPopupState, LocationPopupActions }
